import { useState, useEffect, useCallback } from 'react';
// import { message } from 'antd';

import Card from '@/utils/card';
import { CARD_ERROR_MAP, LockVendor } from '@/components/Cards/config';
import { isBlankCardFn } from '@/components/Cards/util';

import { RoomCardInfo, RoomCardHook } from './interface';

const useRoomCard = ({ hotelId, vendorInfo }): RoomCardHook => {
  const defaultRoomCardInfo: RoomCardInfo = {
    serialnumber: '', // 房卡流水号
    hotelid: null, // 酒店id
    dai: 0, // 代
    effecteddate: '', // 生效时间
    expireddate: '', // 过期时间
    lockno: '', // 锁号
    cardno: '', // 卡号(必达、杨格新增)
  };

  const [cardInstance, setCardInstance] = useState(null);
  const [rafId, setRafId] = useState(0);
  const [roomCardInfo, setRoomCardInfo] = useState(defaultRoomCardInfo);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState(false);
  const [readErrorInfo, setReadErrorInfo] = useState({});
  const [blankCard, setBlankCard] = useState(false);

  // 根据品牌区分读卡参数
  const generateReadParams = () => {
    let result = null;

    if (vendorInfo.lockCode === LockVendor.H21) {
      result = {
        hotelid: hotelId,
      };
    }

    if (vendorInfo.lockCode === LockVendor.BD) {
      result = {
        username: vendorInfo.dbRoot || '',
        password: vendorInfo.dbPass || '',
      };
    }

    if (vendorInfo.lockCode === LockVendor.YG) {
      result = {};
    }

    return result;
  };

  let start;

  // 循环读卡
  const readCard = (timestamp) => {
    if (start === undefined) {
      start = timestamp;
    }

    setRoomCardInfo(defaultRoomCardInfo);
    setReading(true);
    const elapsed = timestamp - start;

    // 500ms gap
    if (elapsed > 500 && cardInstance !== null && cardInstance.connected) {
      const readCardParams = generateReadParams();

      cardInstance
        .readRoomCard(readCardParams)
        .then((data) => {
          // 区分宾客卡和空白卡
          const isBlankCard = isBlankCardFn(vendorInfo, data);
          if (isBlankCard) {
            setBlankCard(true);
          } else {
            setRoomCardInfo(data);
          }

          setReading(false);
          // clear loop
          if (rafId) cancelAnimationFrame(rafId);
        })
        .catch((e) => {
          // if (vendorInfo.lockCode === LockVendor.H21) {
          // 未放卡或卡片未放好
          if (e.code === CARD_ERROR_MAP.noCard) {
            // go on read
            setReadError(false);
            start = undefined;
            setRafId(requestAnimationFrame(readCard));
          } else {
            setReadError(true);
            setReading(false);
            setReadErrorInfo(e);
            // message.error(e.error || '读卡失败');
            // clear loop
            if (rafId) cancelAnimationFrame(rafId);
          }
          // } else {
          //   setReadError(true);
          //   setReading(false);
          //   setReadErrorInfo(e);
          //   // message.error(e.error || '读卡失败');
          //   // clear loop
          //   if (rafId) cancelAnimationFrame(rafId);
          // }
        });
    } else {
      setRafId(requestAnimationFrame(readCard));
    }
  };

  // reset states
  const resetCardStates = () => {
    if (cardInstance !== null && cardInstance.connected) cardInstance.close();
    setCardInstance(null);
    setReadError(false);
    setReading(false);
    setBlankCard(false);
    setRoomCardInfo(defaultRoomCardInfo);
    if (rafId) cancelAnimationFrame(rafId);
  };

  // 开始读卡
  const startRead = useCallback(() => {
    // init ws connect
    const c = new Card();
    setCardInstance(c);
  }, []);

  // 重试
  const retryRead = useCallback(() => {
    setReadError(false);
    setBlankCard(false);
    start = undefined;

    if (cardInstance !== null) {
      setRafId(requestAnimationFrame(readCard));
    } else {
      startRead();
    }
  }, []);

  // init read
  useEffect(() => {
    if (cardInstance !== null) {
      setRafId(requestAnimationFrame(readCard));
    }
  }, [cardInstance]);

  // reset states when unmounted
  useEffect(() => {
    return () => {
      resetCardStates();
    };
  }, []);

  return {
    cardInstance,
    roomCardInfo,
    readError,
    readErrorInfo,
    blankCard,
    reading,
    resetCardStates,
    startRead,
    retryRead,
  };
};

export default useRoomCard;
